import { useState, useEffect, useCallback } from 'react';
import {
  getRecipes,
  getMyRecipes,
  cloneRecipe as cloneRecipeService,
  type Recipe,
  type RecipesMeta
} from '../services/recipeService';
import { getMacSsoToken } from '../services/workflowService';
import { FusionNowNotSubscribedError } from '../services/fusionNowAuth';

export function useRecipes({ perPage = 12 }: { perPage?: number } = {}) {
  // ── Global recipes (community_pages) ─────────────────────────────────────
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [meta, setMeta] = useState<RecipesMeta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── My recipes ────────────────────────────────────────────────────────────
  const [myRecipes, setMyRecipes] = useState<Recipe[]>([]);
  const [myMeta, setMyMeta] = useState<RecipesMeta | null>(null);
  const [isLoadingMy, setIsLoadingMy] = useState(false);
  const [myError, setMyError] = useState<string | null>(null);

  // ── Shared search ─────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // ── Clone state (global recipes) ─────────────────────────────────────────
  const [isCloningKey, setIsCloningKey] = useState<string | null>(null);
  const [cloneSuccess, setCloneSuccess] = useState<string | null>(null);
  const [cloneError, setCloneError] = useState<string | null>(null);

  // ── View In MAC state (my recipes) ────────────────────────────────────────
  const [isViewingInMac, setIsViewingInMac] = useState<string | null>(null);
  const [viewInMacError, setViewInMacError] = useState<string | null>(null);
  const [notSubscribed, setNotSubscribed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchPage = useCallback(async (page: number, search: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const { recipes: data, meta: m } = await getRecipes(page, search, perPage);
      setRecipes(data);
      setMeta(m);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recipes');
    } finally {
      setIsLoading(false);
    }
  }, [perPage]);

  const fetchMyPage = useCallback(async (page: number, search: string) => {
    setIsLoadingMy(true);
    setMyError(null);
    try {
      const { recipes: data, meta: m } = await getMyRecipes(page, search, perPage);
      setMyRecipes(data);
      setMyMeta(m);
    } catch (err) {
      if (err instanceof FusionNowNotSubscribedError) {
        setNotSubscribed(true);
      } else {
        setMyError(
          err instanceof Error ? err.message : 'Failed to load my recipes'
        );
      }
    } finally {
      setIsLoadingMy(false);
    }
  }, [perPage]);

  // Fetch page 1 for both whenever debouncedSearch changes (covers initial load)
  useEffect(() => {
    fetchPage(1, debouncedSearch);
    fetchMyPage(1, debouncedSearch);
  }, [debouncedSearch, fetchPage, fetchMyPage]);

  const fetchRecipes = useCallback(() => {
    fetchPage(meta?.current_page ?? 1, debouncedSearch);
  }, [fetchPage, debouncedSearch, meta]);

  const fetchMyRecipes = useCallback(() => {
    fetchMyPage(myMeta?.current_page ?? 1, debouncedSearch);
  }, [fetchMyPage, debouncedSearch, myMeta]);

  const goToPage = useCallback(
    (page: number) => {
      setSearchQuery('');
      setDebouncedSearch('');
      fetchPage(page, '');
    },
    [fetchPage]
  );

  const goToMyPage = useCallback(
    (page: number) => {
      setSearchQuery('');
      setDebouncedSearch('');
      fetchMyPage(page, '');
    },
    [fetchMyPage]
  );

  const cloneRecipe = useCallback(async (key: string) => {
    setIsCloningKey(key);
    setCloneError(null);
    try {
      await cloneRecipeService(key);
      setCloneSuccess(key);
      setTimeout(() => setCloneSuccess(null), 2000);
    } catch {
      setCloneError(key);
      setTimeout(() => setCloneError(null), 3000);
    } finally {
      setIsCloningKey(null);
    }
  }, []);

  const viewInMac = useCallback(async (key: string) => {
    setIsViewingInMac(key);
    setViewInMacError(null);
    try {
      const amsToken = localStorage.getItem('amsToken') ?? '';
      const ssoToken = await getMacSsoToken(amsToken);
      window.open(
        `${process.env.REACT_APP_FUSIONNOW_APP_URL}/now-certs-auth?token=${encodeURIComponent(ssoToken)}&action=recipe-${key}`,
        '_blank'
      );
    } catch {
      setViewInMacError(key);
      setTimeout(() => setViewInMacError(null), 3000);
    } finally {
      setIsViewingInMac(null);
    }
  }, []);

  return {
    // Global recipes
    recipes,
    meta,
    isLoading,
    error,
    notSubscribed,
    fetchRecipes,
    goToPage,
    // My recipes
    myRecipes,
    myMeta,
    isLoadingMy,
    myError,
    fetchMyRecipes,
    goToMyPage,
    // Shared
    searchQuery,
    setSearchQuery,
    // Clone (global)
    isCloningKey,
    cloneSuccess,
    cloneError,
    cloneRecipe,
    // View In MAC (my)
    isViewingInMac,
    viewInMacError,
    viewInMac
  };
}
